import { Signal, untracked } from '@angular/core';
import type { BannerCapability, BreadcrumbsCapability, SurfaceBreadcrumb, SurfaceHeaderSlotItem, TagDeriverStyle } from '../contracts/surface.capabilities';
import type { CompiledHeaderChip, CompiledHeaderTag } from '../runtime/compiled.form';
import { readModelValue } from '../runtime/model.path';
import type { SurfaceRuntimeResolver } from '../runtime/surface.runtime/surface.runtime.resolver';

/** Host-neutral header tag presentation resolved from a compiled tag. */
export interface SurfaceHeaderTag {
  /** Stable rendering key, normally the tag's model bind. */
  readonly key: string;
  /** Text rendered by the host's tag or badge primitive. */
  readonly label: string;
  /** Optional derived colors; null requests the host's default tag style. */
  readonly style: TagDeriverStyle | null;
  /** Hover text from the deriver, when it gave one. */
  readonly tooltip?: string;
}

/** Host-neutral labelled information chip resolved from a compiled header chip. */
export interface SurfaceHeaderChip {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  readonly style: TagDeriverStyle | null;
}

/** Resolves one compiled header tag against the current model and runtime chain. */
export function resolveSurfaceHeaderTag<TModel = Record<string, unknown>>(tag: CompiledHeaderTag, model: TModel, resolver: SurfaceRuntimeResolver | undefined): SurfaceHeaderTag | null {

  const value = readModelValue(model as Record<string, unknown>, tag.bind);
  if (!tag.deriver) return value == null || value === '' ? null : { key: tag.bind, label: String(value), style: null };
  const style = resolver?.resolve('tags', tag.deriver)?.(value, model);
  return style ? { key: tag.bind, label: style.label, style, ...(style.tooltip ? { tooltip: style.tooltip } : {}) } : null;
}

/** Kind label shown before the identifier; null when absent or it would only repeat the identifier ("Visit · Visit"). */
export function resolveSurfaceHeaderKind(kind: string | undefined, identifier: string): string | null {

  if (!kind) return null;
  return kind.trim().toLowerCase() === identifier.trim().toLowerCase() ? null : kind;
}

/** Resolves one labelled header chip against the current model. */
export function resolveSurfaceHeaderChip<TModel = Record<string, unknown>>(chip: CompiledHeaderChip, model: TModel, resolver: SurfaceRuntimeResolver | undefined): SurfaceHeaderChip | null {

  const value = readModelValue(model as Record<string, unknown>, chip.bind);
  if (!chip.deriver) return value == null || value === '' ? null : { key: chip.id, label: chip.label, value: String(value), style: null };
  const derived = resolver?.resolve('tags', chip.deriver)?.(value, model);
  if (!derived) return null;
  const styled = derived.color || derived.backgroundColor || derived.borderColor ? derived : null;
  return { key: chip.id, label: chip.label, value: derived.label, style: styled };
}

/** Appends an in-surface current crumb and makes the previous crumb go back. */
export function withInSurfaceBreadcrumb(crumbs: readonly SurfaceBreadcrumb[], current: string | null, onBack: () => void): readonly SurfaceBreadcrumb[] {

  if (!current) return crumbs;
  if (crumbs.length === 0) return [{ label: current }];
  const parent = crumbs[crumbs.length - 1];
  return [...crumbs.slice(0, -1), { label: parent.label, action: onBack }, { label: current }];
}

/** Resolves and initializes the nearest breadcrumb capability. */
export function resolveSurfaceBreadcrumbs(resolver: SurfaceRuntimeResolver | undefined): Signal<readonly SurfaceBreadcrumb[]> | undefined {

  if (!resolver) return undefined;
  return untracked(() => {
    let capability: BreadcrumbsCapability | undefined;
    resolver.forEach(runtime => {
      capability ??= runtime.config.breadcrumbs;
    });
    return capability?.();
  });
}

/** Resolves the nearest banner capability. */
export function resolveSurfaceBanner(resolver: SurfaceRuntimeResolver | undefined): BannerCapability | undefined {

  if (!resolver) return undefined;
  let capability: BannerCapability | undefined;
  resolver.forEach(runtime => {
    capability ??= runtime.config.banner;
  });
  return capability;
}

/** Merges header-slot items. Innermost key wins; outer runtimes fill missing keys. */
export function resolveSurfaceHeaderSlot(resolver: SurfaceRuntimeResolver | undefined): readonly SurfaceHeaderSlotItem[] {

  return mergeSlotItems(resolver, 'headerSlot');
}

/** Merges totals-footer items with the same precedence as the header slot. */
export function resolveSurfaceTotalsFooter(resolver: SurfaceRuntimeResolver | undefined): readonly SurfaceHeaderSlotItem[] {

  return mergeSlotItems(resolver, 'totalsFooter');
}

function mergeSlotItems(resolver: SurfaceRuntimeResolver | undefined, slot: 'headerSlot' | 'totalsFooter'): readonly SurfaceHeaderSlotItem[] {

  if (!resolver) return [];
  const items = new Map<string, SurfaceHeaderSlotItem>();
  resolver.forEach(runtime => {
    for (const item of runtime.config[slot] ?? []) {
      if (!items.has(item.key)) items.set(item.key, item);
    }
  });
  return [...items.values()];
}
